import React, { useState, useEffect } from 'react';
import { useClient, useMutation } from 'jsonapi-react';
import { FiMoreHorizontal } from 'react-icons/fi';
import { confirm } from '@/lib/confirm';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
// import { TabNavbar } from './TabNavbar';
import { Error404 } from '@/components/custom/error';
import toast from 'react-hot-toast';
import usePermission from '@/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { DashboardContent } from '@/layouts/dashboard';
import { Taxonomies as TaxonomiesWrapper, TaxonomiesColumn, TaxonomiesItem, TaxonomiesText, TaxonomiesList } from '@/components/custom/taxonomies/taxonomies';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/custom/form';
import { FieldGroup } from '@/components/ui/field';

export const Taxonomies = props => {
  const { uri } = props;

  const { t } = useTranslation();
  const client = useClient();
  const [addTaxonomy] = useMutation('taxonomies');
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [currentTaxonomy, setCurrentTaxonomy] = useState();
  const [taxonomies, setTaxonomies] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [newTaxonomyName, setNewTaxonomyName] = useState('');
  const [editTaxonomyName, setEditTaxonomyName] = useState('');
  const [activeColumn, setActiveColumn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingTaxonomy, setAddingTaxonomy] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await client.fetch(`taxonomies`);
      setTaxonomies(data);
      setLoading(false);
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const selectTaxonomy = (taxonomy, currentList, nextList) => {
    let newTopics = taxonomy['taxonomy-children'] || [];

    setTaxonomies([...taxonomies]);
    if (nextList === 'subTopics') {
      topics.map(t => (t.isActive = false));
      taxonomy.isActive = true;
      setSubTopics([...newTopics]);
    } else if (nextList === 'topics') {
      taxonomies.map(t => (t.isActive = false));
      taxonomy.isActive = true;
      setTopics([...newTopics]);
      setSubTopics([]);
    } else {
      subTopics.map(t => (t.isActive = false));
      taxonomy.isActive = true;
      setSubTopics([...subTopics]);
    }
  };

  const newTaxonomyForm = currentList => {
    setActiveColumn(currentList);
    setShowModal(true);
  };

  const editTaxonomyForm = (currentList, taxonomy) => {
    setCurrentTaxonomy(taxonomy);
    setActiveColumn(currentList);
    setEditTaxonomyName(taxonomy.name);
    setEditModal(true);
  };

  const destroyTaxonomyForm = async (currentList, taxonomy) => {
    const confirmed = await confirm({
      title: t('taxonomyAndMaterials.warningDeleteTaxonomy'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      destroyCurrentTaxonomy(currentList, taxonomy);
    }
  };

  const destroyCurrentTaxonomy = async (currentList, taxonomy) => {
    const { error, errors } = await client.delete(['taxonomies', taxonomy.id]);

    if (error && error?.id === 'base') {
      toast.error(`${error.title}`);
    } else if (error) {
      toast.error(t('toast.errorTryAgain'));
    } else {
      if (currentList === 'topics') {
        let newList = topics;
        newList = newList.filter(item => item.id !== taxonomy.id);
        setTopics([...newList]);
      } else if (currentList === 'subTopics') {
        let newList = subTopics;
        newList = newList.filter(item => item.id !== taxonomy.id);
        setSubTopics([...newList]);
      } else {
        let newList = taxonomies;
        newList = newList.filter(item => item.id !== taxonomy.id);
        setTaxonomies([...newList]);
      }
      toast.success('A taxonomia foi deletada.');
    }
  };

  const editCurrentTaxonomy = async e => {
    e.preventDefault();

    const { data, error } = await client.mutate(['taxonomies', currentTaxonomy.id], {
      name: editTaxonomyName
    });

    if (error) {
      toast.error('Erro ao editar taxonomia.');
    } else {
      const newTaxonomyList = updateTaxonomyList(taxonomies, currentTaxonomy.id, data);
      setTaxonomies(newTaxonomyList);

      setEditModal(false);
      setCurrentTaxonomy(null);
      setEditTaxonomyName('');
      toast.success(t('toast.successEditTaxonomy'));
    }
  };

  const updateTaxonomyList = (list, id, newItem) => {
    return list.map(item => {
      if (item.id === id) {
        item.name = newItem.name;
      } else if (item['taxonomy-children']) {
        item['taxonomy-children'] = updateTaxonomyList(item['taxonomy-children'], id, newItem);
      }

      return item;
    });
  };

  const findTaxonomyById = (taxonomies, id) => {
    for (const taxonomy of taxonomies) {
      if (taxonomy.id === id) return taxonomy;
      if (taxonomy['taxonomy-children']) {
        const found = findTaxonomyById(taxonomy['taxonomy-children'], id);
        if (found) return found;
      }
    }
    return null;
  };

  const addNewTaxonomy = async e => {
    e.preventDefault();
    setAddingTaxonomy(true);

    let taxonomyParentId;
    if (activeColumn === 'topics') {
      taxonomyParentId = taxonomies.find(item => item.isActive)?.id;
    } else if (activeColumn === 'subTopics') {
      taxonomyParentId = topics.find(item => item.isActive)?.id;
    }

    addTaxonomy({
      name: newTaxonomyName,
      taxonomyParentId: taxonomyParentId
    }).then(async ({ data: newTaxonomy, error }) => {
      if (error) {
        toast.error(t('toast.errorCreateTaxonomy'));
      } else {
        const updatedTaxonomies = await fetchData();

        const addedTaxonomy = findTaxonomyById(updatedTaxonomies, newTaxonomy.id);

        const findParentHierarchy = (taxonomies, topicId) => {
          for (const taxonomy of taxonomies) {
            if (taxonomy['taxonomy-children']) {
              const topic = taxonomy['taxonomy-children'].find(t => t.id === topicId);
              if (topic) return { parentTaxonomy: taxonomy, parentTopic: topic };
              const nested = findParentHierarchy(taxonomy['taxonomy-children'], topicId);
              if (nested) return nested;
            }
          }
          return null;
        };

        if (addedTaxonomy) {
          if (taxonomyParentId && activeColumn === 'topics') {
            const activeTaxonomy = updatedTaxonomies.find(t => t.id === taxonomyParentId);
            const activatedTaxonomies = updatedTaxonomies.map(taxonomy => ({
              ...taxonomy,
              isActive: taxonomy.id === activeTaxonomy.id
            }));
            setTaxonomies(activatedTaxonomies);
            setTopics(activeTaxonomy['taxonomy-children'] || []);
          } else if (taxonomyParentId && activeColumn === 'subTopics') {
            const hierarchy = findParentHierarchy(updatedTaxonomies, taxonomyParentId);

            if (hierarchy) {
              const activatedTaxonomies = updatedTaxonomies.map(taxonomy => ({
                ...taxonomy,
                isActive: taxonomy.id === hierarchy.parentTaxonomy.id,
                'taxonomy-children': taxonomy['taxonomy-children']?.map(topic => ({
                  ...topic,
                  isActive: topic.id === hierarchy.parentTopic.id
                }))
              }));

              setTaxonomies(activatedTaxonomies);

              setTopics(activatedTaxonomies.find(t => t.isActive)?.['taxonomy-children'] || []);
              setSubTopics(hierarchy.parentTopic['taxonomy-children'] || []);
            }
          } else {
            const activatedTaxonomies = updatedTaxonomies.map(taxonomy => ({
              ...taxonomy,
              isActive: taxonomy.id === addedTaxonomy.id
            }));
            setTaxonomies(activatedTaxonomies);
          }
        }

        setShowModal(false);
        setNewTaxonomyName('');
        toast.success(t('toast.successCreateTaxonomy'));
      }
      setAddingTaxonomy(false);
    });
  };

  const breadcrumbs = {
    title: 'Organização da Plataforma',
    nav: [
      {
        route: uri,
        name: 'Ementa',
        isActive: true
      }
    ]
  };

  return usePermission('Visualizar lista de ementas, tópicos e subtópicos', 'Ementas') ? (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* <TabNavbar selectedTab="taxonomies" /> */}

      <TaxonomiesWrapper>
        <TaxonomiesColumn>
          {usePermission('Criar ementas', 'Ementas') && (
            <Button
              variant="outline"
              fullWidth
              className="mb-8"
              onClick={() => newTaxonomyForm('taxonomies')}
            >
              {t('taxonomyAndMaterials.buttonAddTaxonomy')}
            </Button>
          )}

          {loading && <Loader />}

          <TaxonomiesList>
            {taxonomies &&
              taxonomies.map((taxonomy, index) => (
                <TaxonomiesItem
                  key={index}
                  active={taxonomy.isActive}
                  onClick={() => selectTaxonomy(taxonomy, 'taxonomies', 'topics')}
                >
                  <TaxonomiesText>{taxonomy.name}</TaxonomiesText>

                  {usePermission('Editar ementas', 'Ementas') || usePermission('Excluir ementas', 'Ementas') ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger className="inline-flex">
                        <FiMoreHorizontal size={16} />
                      </DropdownMenuTrigger>

                      <div onClick={e => e.stopPropagation()}>
                        <DropdownMenuContent align="end">
                          {usePermission('Editar ementas', 'Ementas') && <DropdownMenuItem onSelect={() => editTaxonomyForm('taxonomies', taxonomy)}>{t('button.edit')}</DropdownMenuItem>}
                          {usePermission('Excluir ementas', 'Ementas') && (
                            <DropdownMenuItem
                              onSelect={() => destroyTaxonomyForm('taxonomies', taxonomy)}
                              className="text-danger"
                            >
                              {t('button.delete')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </div>
                    </DropdownMenu>
                  ) : null}
                </TaxonomiesItem>
              ))}
          </TaxonomiesList>
        </TaxonomiesColumn>

        <TaxonomiesColumn>
          {usePermission('Criar ementas', 'Ementas') && taxonomies.some(t => t.isActive) && (
            <Button
              variant="outline"
              fullWidth
              className="mb-8"
              onClick={() => newTaxonomyForm('topics')}
            >
              {t('taxonomyAndMaterials.buttonAddTaxonomy')}
            </Button>
          )}

          <TaxonomiesList>
            {topics &&
              topics.map((taxonomy, index) => (
                <TaxonomiesItem
                  key={index}
                  active={taxonomy.isActive}
                  onClick={() => selectTaxonomy(taxonomy, 'topics', 'subTopics')}
                >
                  <TaxonomiesText>{taxonomy.name}</TaxonomiesText>

                  <DropdownMenu>
                    <DropdownMenuTrigger className="inline-flex">{usePermission('Editar ementas', 'Ementas') && <FiMoreHorizontal size={16} />}</DropdownMenuTrigger>

                    <div onClick={e => e.stopPropagation()}>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => editTaxonomyForm('taxonomies', taxonomy)}>{t('button.edit')}</DropdownMenuItem>

                        <DropdownMenuItem
                          onSelect={() => destroyTaxonomyForm('topics', taxonomy)}
                          className="text-danger"
                        >
                          Deletar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </div>
                  </DropdownMenu>
                </TaxonomiesItem>
              ))}

            {!loading && topics.length === 0 && (
              <EmptyState
                type="select"
                text={t('emptyState.selectTopicText')}
              />
            )}
          </TaxonomiesList>
        </TaxonomiesColumn>

        <TaxonomiesColumn>
          {topics.some(t => t.isActive) && (
            <Button
              variant="outline"
              fullWidth
              className="mb-8"
              onClick={() => newTaxonomyForm('subTopics')}
            >
              {t('taxonomyAndMaterials.buttonAddTaxonomy')}
            </Button>
          )}

          <TaxonomiesList>
            {subTopics &&
              subTopics.map((taxonomy, index) => (
                <TaxonomiesItem key={index}>
                  <TaxonomiesText>{taxonomy.name}</TaxonomiesText>

                  <DropdownMenu>
                    <DropdownMenuTrigger className="inline-flex">
                      <FiMoreHorizontal size={16} />
                    </DropdownMenuTrigger>

                    <div onClick={e => e.stopPropagation()}>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => editTaxonomyForm('taxonomies', taxonomy)}>{t('button.edit')}</DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => destroyTaxonomyForm('subTopics', taxonomy)}
                          className="text-danger"
                        >
                          Deletar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </div>
                  </DropdownMenu>
                </TaxonomiesItem>
              ))}

            {!loading && subTopics.length === 0 && (
              <EmptyState
                type="select"
                text={t('emptyState.selectSubtopicText')}
              />
            )}
          </TaxonomiesList>
        </TaxonomiesColumn>

        <Dialog
          open={showModal}
          onOpenChange={() => {
            setShowModal(false);
            setNewTaxonomyName('');
          }}
        >
          <DialogContent>
            <form
              method="post"
              onSubmit={addNewTaxonomy}
            >
              <FieldGroup>
                <DialogTitle>{t('taxonomyAndMaterials.createTaxonomy')}</DialogTitle>
                <DialogDescription className="sr-only">Formulário para criação de taxonomia.</DialogDescription>

                <FormField
                  label={t('textsCommon.name')}
                  name="name"
                  type="text"
                  placeholder={t('taxonomyAndMaterials.placeholderTaxonomyName')}
                  value={newTaxonomyName}
                  onChange={e => setNewTaxonomyName(e.target.value)}
                />

                <div>
                  <Button
                    type="submit"
                    disabled={addingTaxonomy || !newTaxonomyName}
                  >
                    {t('taxonomyAndMaterials.buttonCreateNewTaxonomy')}
                  </Button>
                </div>
              </FieldGroup>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={editModal}
          onOpenChange={() => setEditModal(false)}
        >
          <DialogContent>
            <form
              method="patch"
              onSubmit={editCurrentTaxonomy}
            >
              <FieldGroup>
                <DialogTitle>{t('taxonomyAndMaterials.buttonEditTaxonomy')}</DialogTitle>
                <DialogDescription className="sr-only">Formulário para edição de taxonomia.</DialogDescription>

                <FormField
                  label={t('textsCommon.name')}
                  name="name"
                  type="text"
                  placeholder={t('taxonomyAndMaterials.placeholderNewTaxonomyName')}
                  value={editTaxonomyName}
                  onChange={e => setEditTaxonomyName(e.target.value)}
                />

                <div>
                  <Button
                    type="submit"
                    disabled={addingTaxonomy}
                  >
                    {t('taxonomyAndMaterials.buttonEditTaxonomy')}
                  </Button>
                </div>
              </FieldGroup>
            </form>
          </DialogContent>
        </Dialog>
      </TaxonomiesWrapper>
    </DashboardContent>
  ) : (
    <Error404 />
  );
};
